import ActivityCard from './activityCard';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import NoData from '../dashboard/noData';
import { NoteEmpty, Slash } from '../icons';
import { Spin } from '../Ant';
import { ITask } from '../../interface/tasks';
import { useInView } from 'react-intersection-observer';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import { useWorkTime } from '../../context/WorkTimeContext';
import { IWorkTime } from '../../interface/workTimes';
import { useActivityData } from '../../hooks/useActivityData';
import { useActivityFilter } from './useActivityFilter';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import { useDiscardDialog } from '../../hooks/useDiscardDialog';
import { isWorkTimeFormChanged } from './isWorkTimeFormChanged';
import CustomModal from '../informationPopup/customModal';

const ActivityListing = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { updateWorkTime, workTime, initialWorkTime } = useWorkTime();
  const linkedMatter = workTime?.matter;
  const [searchValue, setSearchValue] = useState<string>('');
  const { setTitle } = usePage();
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const { filterOptions, selectedFilter, selectFilter, clearFilter } = useActivityFilter();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
    refreshActivityList,
  } = useActivityData({
    selectedFilter,
    searchValue,
    linkedMatter,
    user,
  });
  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    promptDiscardIfChanged(isWorkTimeFormChanged(workTime, initialWorkTime, !!workTime?.id), () => {
      updateWorkTime(null);
      goBack(undefined, 2);
    });
  };
  const handleSelectActivity = (activity: ITask) => {
    updateWorkTime({
      ...workTime,
      activity,
      activityId: activity?.id,
      ...(activity?.matter && { matter: activity?.matter, matterId: activity?.matter?.id }),
    } as IWorkTime);
    goBackWithSelection();
  };
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && data?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('worktime:selectActivity')}
        showCross
      />,
    );
  }, []);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };
  return (
    <>
      <CollapsibleListLayout
        testId="activity-listing"
        header={
          <SearchInputWithFilters
            searchTitle={t('task:search')}
            handleSearch={(val) => {
              setSearchValue(val);
            }}
            handleRefresh={refreshActivityList}
            filterTitle={t('worktime:activityFilters.label')}
            radioOptions={filterOptions}
            selectedFilter={[selectedFilter]}
            setSelectedFilter={selectFilter}
            clearFilter={clearFilter}
          />
        }
      >
        {data &&
          data.length > 0 &&
          data.map((activity: ITask) => {
            return (
              <ActivityCard
                key={activity?.id}
                activity={activity}
                isSelected={workTime?.activity?.id === activity?.id}
                onSelect={() => handleSelectActivity(activity)}
              />
            );
          })}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {(!data || (Object.entries(data).length === 0 && !isLoading)) && (
          <div className="flex-grow d-flex justify-center items-center">
            <NoData
              header={t('task:noTasks')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<NoteEmpty />}
            />
          </div>
        )}
        {isLoading ? <SkeletonCardList rows={[1, 1]} /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default ActivityListing;
